/*
 * Taste test cards.
 *
 * The clips autoplay muted so the section reads as three moving testimonials
 * rather than three thumbnails waiting on a click. Three things need
 * scripting: fetching a clip only once its card is close to the viewport,
 * only playing what is actually on screen, and letting someone turn the
 * sound on for the one they are watching.
 *
 * The markup carries no autoplay and no src — with either present the browser
 * downloads every clip on page load, which on the product page meant 16 MB of
 * video on a phone before the visitor had read a word. Sources live in
 * data-src and the poster in data-poster until "warm" attaches them.
 */
(function () {
  'use strict';

  var cards = document.querySelectorAll('[data-taste-card]');
  if (!cards.length) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function videoIn(card) {
    return card.querySelector('video');
  }

  /* Attach the real sources. Idempotent: the second observer and the
     fallbacks may all ask for it. preload flips to metadata so the first
     frame arrives and stands in as the poster where no still is set. */
  function warm(video) {
    if (!video || video.dataset.warm) return;
    video.dataset.warm = '1';

    var poster = video.getAttribute('data-poster');
    if (poster) video.poster = poster;

    var sources = video.querySelectorAll('source[data-src]');
    Array.prototype.forEach.call(sources, function (source) {
      source.src = source.getAttribute('data-src');
    });

    if (sources.length) {
      video.preload = 'metadata';
      video.load();
    }
  }

  /* Autoplay is a promise in every current browser and rejects on its own when
     the tab is backgrounded or the clip is not ready yet. Swallow it — a failed
     play leaves the poster up, which is a fine resting state. */
  function play(video) {
    warm(video);
    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
  }

  cards.forEach(function (card) {
    var video = videoIn(card);
    if (!video) return;

    // Shopify's video_tag can emit controls even when told not to.
    video.controls = false;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');

    var button = card.querySelector('[data-taste-sound]');
    if (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();

        var turningOn = video.muted;

        // One clip at a time, or three people talk over each other.
        if (turningOn) {
          cards.forEach(function (other) {
            if (other === card) return;
            var otherVideo = videoIn(other);
            var otherButton = other.querySelector('[data-taste-sound]');
            if (otherVideo) otherVideo.muted = true;
            if (otherButton) {
              otherButton.setAttribute('aria-pressed', 'false');
              other.classList.remove('is-unmuted');
            }
          });
        }

        video.muted = !turningOn;
        button.setAttribute('aria-pressed', turningOn ? 'true' : 'false');
        card.classList.toggle('is-unmuted', turningOn);
        if (turningOn) play(video);
      });
    }
  });

  /* Reduced motion: no autoplay, but the poster should still show, so warm
     the clips once they are near — the sound button plays on demand. */
  if (!('IntersectionObserver' in window)) {
    cards.forEach(function (card) {
      var video = videoIn(card);
      if (!video) return;
      if (reduceMotion) warm(video);
      else play(video);
    });
    return;
  }

  /* One viewport ahead: fetch metadata and the poster so the first frame is
     on screen by the time the card scrolls in. */
  var warmer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        warm(videoIn(entry.target));
        warmer.unobserve(entry.target);
      });
    },
    { rootMargin: '100% 0px' }
  );

  cards.forEach(function (card) {
    warmer.observe(card);
  });

  if (reduceMotion) return;

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        var video = videoIn(entry.target);
        if (!video) return;
        if (entry.isIntersecting) {
          play(video);
        } else {
          video.pause();
          // Sound follows the clip off screen, so scrolling away silences it.
          if (!video.muted) {
            video.muted = true;
            var button = entry.target.querySelector('[data-taste-sound]');
            if (button) button.setAttribute('aria-pressed', 'false');
            entry.target.classList.remove('is-unmuted');
          }
        }
      });
    },
    { threshold: 0.25 }
  );

  cards.forEach(function (card) {
    observer.observe(card);
  });
})();
